
import React from 'react';
import { EstateAssets, PensionAsset } from '../../../types';
import { PiggyBank, Trash2, Plus, HelpCircle } from 'lucide-react';
import { HelpPopover } from '../../HelpPopover';
import { HELP_CONTENT } from '../../helpContent';

interface Props {
    data: EstateAssets;
    onChange: (assets: EstateAssets) => void;
}

export const PensionsStep: React.FC<Props> = ({ data, onChange }) => {
    const items = data.pensions || [];

    const updateItem = (index: number, changes: Partial<PensionAsset>) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], ...changes };
        onChange({ ...data, pensions: newItems });
    };

    const addItem = () => {
        const newItem: PensionAsset = {
            id: `pen_${Date.now()}`,
            label: '',
            provider: '',
            in_estate: null, // Not sure
            estimated_value: null
        };
        onChange({ ...data, pensions: [...items, newItem] });
    };

    const removeItem = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        onChange({ ...data, pensions: newItems });
    };

    React.useEffect(() => {
        if (items.length === 0) addItem();
    }, []);

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-ink">Pensions</h2>
                <p className="text-ink3 mt-2">
                    Most pensions are tax-free, but we need to check how they are paid out.
                </p>
            </div>

            <div className="space-y-6">
                {items.map((item, idx) => (
                    <div key={item.id} className="bg-white p-6 rounded-xl border border-rule relative group">
                        {items.length > 1 && (
                            <button
                                onClick={() => removeItem(idx)}
                                className="absolute top-4 right-4 text-sub hover:text-ink2 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}

                        <div className="mb-6">
                            <label className="block text-xs font-bold text-ink3 uppercase mb-1">Pension Provider</label>
                            <div className="flex items-center gap-2">
                                <PiggyBank className="w-5 h-5 text-ink3" />
                                <input
                                    type="text"
                                    placeholder="e.g. Scottish Widows"
                                    className="w-full border-b border-rule focus:border-accent focus:outline-none py-1 text-ink font-medium"
                                    value={item.provider || ''}
                                    onChange={(e) => updateItem(idx, { provider: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Critical Question */}
                        <div className="mb-6 bg-paper p-4 rounded-lg border border-rule-soft">
                            <label className="block text-sm font-bold text-ink mb-2 flex items-center gap-2">
                                Is this pension likely to be paid directly to beneficiaries (outside the estate)?
                                <HelpPopover content={HELP_CONTENT.PENSIONS_IN_ESTATE} />
                            </label>

                            <div className="space-y-3">
                                {[
                                    { label: 'Yes (usually outside estate)', val: false, sub: 'Pays directly to a named person — typically excluded from IHT.' },
                                    { label: 'No (paid into estate)', val: true, sub: 'Forms part of the estate for IHT.' },
                                    { label: 'Not sure', val: null, sub: "We'll assume excluded for now and flag for review." }
                                ].map((opt, i) => (
                                    <label key={i} className={`
                                        flex items-center p-3 rounded-lg border cursor-pointer transition-all
                                        ${item.in_estate === opt.val
                                            ? 'bg-accent-soft border-accent'
                                            : 'bg-white border-rule hover:bg-paper'}
                                    `}>
                                        <input
                                            type="radio"
                                            name={`pension_status_${item.id}`}
                                            className="w-4 h-4 text-accent focus:ring-accent"
                                            checked={item.in_estate === opt.val}
                                            onChange={() => updateItem(idx, { in_estate: opt.val as any })}
                                        />
                                        <div className="ml-3">
                                            <span className={`block text-sm font-bold ${item.in_estate === opt.val ? 'text-ink' : 'text-ink2'}`}>
                                                {opt.label}
                                            </span>
                                            <span className="block text-xs text-ink3">
                                                {opt.sub}
                                            </span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-ink3 uppercase mb-1">Estimated Pot Value (£)</label>
                            <input
                                type="number"
                                className="w-full sm:w-1/2 border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-accent focus:outline-none"
                                placeholder="0.00"
                                value={item.estimated_value ?? ''}
                                onChange={(e) => updateItem(idx, { estimated_value: e.target.value ? Number(e.target.value) : null })}
                            />
                        </div>
                    </div>
                ))}

                <button
                    onClick={addItem}
                    className="w-full py-3 border-2 border-dashed border-rule rounded-lg text-ink3 font-bold hover:border-accent hover:text-accent transition-all flex items-center justify-center gap-2 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Another Pension
                </button>
            </div>
        </div>
    );
};
